import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import swal from "sweetalert2";
import { ProyectosService } from "src/app/proyectos/proyectos.service";

interface DetalleServicioDialogData {
  idProyecto: string;
  titulo?: string;
  categoria?: string;
  cliente?: string;
}

interface ComentarioSeguimiento {
  id: number;
  fecha: string;
  descripcion: string;
  nombreUsuario: string;
  horasAgregadas: number | null;
  horasAcumuladasTrasRegistro: number | null;
}

@Component({
  selector: "app-detalle-servicio",
  standalone: true,
  templateUrl: "./detalle-servicio.component.html",
  styleUrls: ["./detalle-servicio.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
})
export class DetalleServicioComponent implements OnInit {
  readonly categoria: string;

  titulo: string;
  cliente: string;
  aplicacion = "";
  idProyecto = "";
  horasContratadas = 0;
  horasUsadasCalculadas = 0;
  horasUsadas = 0;
  saldoHoras = 0;
  fechaInicio = "";
  fechaFin = "";

  isLoading = false;
  loadError = false;
  comentarios: ComentarioSeguimiento[] = [];

  observaciones = "";
  horasAdicionales = 0;
  isSaving = false;
  private huboCambios = false;

  constructor(
    public dialogRef: MatDialogRef<DetalleServicioComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DetalleServicioDialogData,
    private proyectosService: ProyectosService,
    private cdr: ChangeDetectorRef
  ) {
    // titulo/cliente se muestran de inmediato con lo que mande el llamador (puede venir
    // vacío, ej. desde Gestión de Proyectos) y se sobrescriben con el valor autoritativo
    // en cuanto responde consultarDetalleSeguimientoProyecto (ver cargarHistorial).
    this.titulo = data?.titulo ?? "";
    this.categoria = data?.categoria ?? "";
    this.cliente = data?.cliente ?? "";
  }

  get diferenciaCos(): number {
    return Math.abs(this.horasUsadasCalculadas - this.horasUsadas);
  }

  get disponiblesTrasRegistrar(): number {
    return this.saldoHoras - (this.horasAdicionales || 0);
  }

  get utilizadasTrasRegistrar(): number {
    return this.horasUsadas + (this.horasAdicionales || 0);
  }

  // Un servicio sin hrs contratadas es un escenario válido (p. ej. servicios sin tope de
  // horas); en ese caso el concepto de "saldo/disponibles" no aplica y se muestra
  // "Sin límite" en vez de un número negativo o engañoso.
  get tieneLimiteContratado(): boolean {
    return this.horasContratadas > 0;
  }

  get saldoLabel(): string {
    return this.tieneLimiteContratado ? `${this.saldoHoras} hrs` : "Sin límite";
  }

  get disponiblesTrasRegistrarLabel(): string {
    return this.tieneLimiteContratado ? `${this.disponiblesTrasRegistrar} hrs` : "Sin límite";
  }

  saldoHistorialLabel(comentario: ComentarioSeguimiento): string {
    if (!this.tieneLimiteContratado) {
      return "SIN LÍMITE";
    }
    const saldo = this.horasContratadas - (comentario.horasAcumuladasTrasRegistro || 0);
    return `${saldo} HRS`;
  }

  get puedeRegistrar(): boolean {
    return !!this.observaciones?.trim() && !this.isSaving;
  }

  ngOnInit(): void {
    this.cargarHistorial();
  }

  incrementar(): void {
    this.horasAdicionales = (this.horasAdicionales || 0) + 1;
  }

  decrementar(): void {
    this.horasAdicionales = Math.max(0, (this.horasAdicionales || 0) - 1);
  }

  private cargarHistorial(): void {
    this.isLoading = true;
    this.loadError = false;

    this.proyectosService.consultarDetalleSeguimientoProyecto(this.data.idProyecto).subscribe({
      next: (response) => {
        this.comentarios = response?.comentariosAvances ?? [];
        this.titulo = response?.title || this.titulo;
        this.aplicacion = response?.aplicacion || "";
        this.cliente = response?.client || this.cliente;
        this.idProyecto = response?.idProyecto || "";
        this.horasContratadas = Math.round(response?.horasTotales ?? 0);
        this.horasUsadasCalculadas = Math.round(response?.horasReales ?? 0);
        this.horasUsadas = response?.horasUsadasManual ?? 0;
        this.saldoHoras = this.horasContratadas - this.horasUsadas;
        this.fechaInicio = this.formatFecha(response?.fechaInicio);
        this.fechaFin = this.formatFecha(response?.fechaFin);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error("Error al consultar el historial de seguimiento del servicio:", error);
        this.comentarios = [];
        this.loadError = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  guardar(): void {
    if (!this.observaciones?.trim()) {
      swal.fire("Atención", "Debes escribir una observación", "warning");
      return;
    }

    const horasAdicionales = Math.max(0, Math.floor(this.horasAdicionales || 0));

    this.isSaving = true;
    this.proyectosService
      .guardarSeguimientoServicio({
        idProyecto: this.data.idProyecto,
        descripcion: this.observaciones.trim(),
        horasAdicionales,
      })
      .subscribe({
        next: () => {
          this.huboCambios = true;
          this.horasUsadas += horasAdicionales;
          this.saldoHoras -= horasAdicionales;
          this.observaciones = "";
          this.horasAdicionales = 0;
          this.isSaving = false;
          this.cargarHistorial();
        },
        error: () => {
          this.isSaving = false;
          swal.fire("Error", "No se pudo guardar el seguimiento. Intenta de nuevo.", "error");
        },
      });
  }

  close(): void {
    this.dialogRef.close(this.huboCambios);
  }

  private formatFecha(fecha: string | null | undefined): string {
    if (!fecha) {
      return "Sin fecha";
    }
    const valor = new Date(fecha);
    if (Number.isNaN(valor.getTime())) {
      return "Sin fecha";
    }
    return valor.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
}
